import type { JSX } from 'react';
import { useCallback, useMemo, useReducer, useRef, useState } from 'react';
import { Button } from '@/app/ui/controls';
import { getTemplate } from '@/catalogs/templates';
import { compose } from '@/core/pipeline';
import { serializeProject, parseProjectFile, projectFileName } from '@/storage/projectFile';
import { createDefaultProject } from '@/core/project/defaults';
import { useDebounced, useOutlineFont, usePipeline } from './hooks';
import { IssueList } from './IssueList';
import { ScenePreview } from './ScenePreview';
import { DataPanel } from './panels/DataPanel';
import { DesignPanel } from './panels/DesignPanel';
import { ExportPanel } from './panels/ExportPanel';
import { TextPanel } from './panels/TextPanel';
import { canRedo, canUndo, createInitialState, editorReducer } from './state';
import { ExportBar } from './ExportBar';
import { useExportJob } from './useExportJob';
import styles from './Editor.module.css';

type TabId = 'data' | 'design' | 'text' | 'export';

const TABS: { id: TabId; label: string }[] = [
  { id: 'data', label: 'Данные' },
  { id: 'design', label: 'Оформление' },
  { id: 'text', label: 'Текст' },
  { id: 'export', label: 'Экспорт' },
];

export function Editor(): JSX.Element {
  const [state, dispatch] = useReducer(editorReducer, undefined, createInitialState);
  const [tab, setTab] = useState<TabId>('data');
  const fileInput = useRef<HTMLInputElement>(null);
  const [importError, setImportError] = useState<string | null>(null);

  // Предпросмотр чуть отстаёт от ввода, чтобы набор текста не тормозил.
  const project = useDebounced(state.project);
  const template = getTemplate(project.template.id);
  const needsFont =
    project.output.includeDecoration &&
    template.blocks.some(
      (b) =>
        (b.kind === 'title' && project.title.trim() !== '') ||
        (b.kind === 'caption' && project.caption.text.trim() !== '') ||
        (b.kind === 'note' && project.note.trim() !== ''),
    );

  const fontState = useOutlineFont(project.caption.fontId, project.caption.weight, needsFont);
  const font = fontState.status === 'ready' ? fontState.font : null;
  const { content, encoded, scene } = usePipeline(project, font);
  const exportJob = useExportJob(state.revision);

  const layoutIssues = scene ? scene.warnings : [];
  const blocking = layoutIssues.some((w) => w.level === 'error');
  const ready = scene !== null && !blocking;

  const startExport = useCallback(() => {
    // Экспорт всегда пересобирает макет по последнему подтверждённому
    // состоянию, а не по отстающему предпросмотру: иначе только что
    // изменённый формат или размер попал бы в файл с опозданием.
    const fresh = compose(state.project, font);
    if (!fresh.scene || fresh.scene.warnings.some((w) => w.level === 'error')) return;
    exportJob.run(fresh.scene, state.project, state.revision);
  }, [exportJob, font, state.project, state.revision]);

  const saveProject = useCallback(() => {
    const blob = new Blob([serializeProject(state.project)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = projectFileName();
    link.click();
    // Отзываем URL только после того, как переход по ссылке уже начался.
    window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }, [state.project]);

  const openProject = useCallback(async (file: File) => {
    const text = await file.text();
    const result = parseProjectFile(text, createDefaultProject());
    if (result.status === 'error') {
      setImportError(result.message);
      return;
    }
    setImportError(null);
    dispatch({ type: 'load-project', project: result.project, notes: result.notes });
  }, []);

  const encodedPreview = useMemo(() => {
    if (content.encoded === '') return null;
    return content.encoded;
  }, [content.encoded]);

  return (
    <div className={styles.layout}>
      <section className={styles.controls} aria-label="Настройки">
        <div className={styles.tabs} role="tablist" aria-label="Разделы настроек">
          {TABS.map((item) => (
            <button
              key={item.id}
              role="tab"
              type="button"
              id={`tab-${item.id}`}
              aria-selected={tab === item.id}
              aria-controls={`panel-${item.id}`}
              className={tab === item.id ? styles.tabActive : styles.tab}
              onClick={() => setTab(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>

        {state.notes.length > 0 ? (
          <div className={styles.notes} role="status">
            <p className={styles.notesTitle}>Проект загружен с замечаниями:</p>
            <ul>
              {state.notes.map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
            <Button variant="ghost" onClick={() => dispatch({ type: 'dismiss-notes' })}>
              Понятно
            </Button>
          </div>
        ) : null}

        {importError ? (
          <p className={styles.importError} role="alert">
            {importError}
          </p>
        ) : null}

        <div
          role="tabpanel"
          id={`panel-${tab}`}
          aria-labelledby={`tab-${tab}`}
          className={styles.panelBox}
        >
          {tab === 'data' ? (
            <DataPanel project={state.project} content={content} encoded={encoded} dispatch={dispatch} />
          ) : null}
          {tab === 'design' ? <DesignPanel project={state.project} dispatch={dispatch} /> : null}
          {tab === 'text' ? (
            <TextPanel project={state.project} fontState={fontState} dispatch={dispatch} />
          ) : null}
          {tab === 'export' ? (
            <ExportPanel
              project={state.project}
              documentHeight={scene?.height ?? null}
              dispatch={dispatch}
            />
          ) : null}
        </div>

        <div className={styles.projectActions}>
          <Button onClick={saveProject} disabled={state.project.payload.value === ''}>
            Сохранить проект (JSON)
          </Button>
          <Button onClick={() => fileInput.current?.click()}>Открыть проект</Button>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            className="visually-hidden"
            aria-label="Файл проекта"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) void openProject(file);
              e.target.value = '';
            }}
          />
          <Button variant="ghost" onClick={() => dispatch({ type: 'undo' })} disabled={!canUndo(state)}>
            Отменить
          </Button>
          <Button variant="ghost" onClick={() => dispatch({ type: 'redo' })} disabled={!canRedo(state)}>
            Повторить
          </Button>
          <Button variant="ghost" onClick={() => dispatch({ type: 'reset' })}>
            Начать заново
          </Button>
        </div>
        <p className={styles.storageNote}>
          Файл проекта содержит введённое вами содержимое QR. Сохраняйте его так же внимательно, как
          сам адрес.
        </p>
      </section>

      {/* Превью и кнопка сохранения живут в одной колонке: иначе липкое
          превью съезжает поверх кнопки и перехватывает нажатия. */}
      <div className={styles.side}>
        <section className={styles.preview} aria-label="Предпросмотр">
          <div className={styles.previewStage}>
            {scene ? (
              <div
                className={styles.previewFrame}
                data-transparent={scene.transparent ? 'true' : 'false'}
                style={{ aspectRatio: `${scene.width} / ${scene.height}` }}
              >
                <ScenePreview scene={scene} className={styles.previewSvg} />
              </div>
            ) : (
              <p className={styles.empty}>
                {state.project.payload.value === ''
                  ? 'Вставьте ссылку или текст — QR появится сразу.'
                  : 'QR не построен: исправьте ошибки в разделе «Данные».'}
              </p>
            )}
          </div>

          {scene ? (
            <dl className={styles.facts}>
              <div>
                <dt>Версия символа</dt>
                <dd>
                  {scene.meta.version} ({scene.meta.matrixSize}×{scene.meta.matrixSize} модулей)
                </dd>
              </div>
              <div>
                <dt>Размер модуля</dt>
                <dd>{scene.meta.moduleSize} px</dd>
              </div>
              <div>
                <dt>Документ</dt>
                <dd>
                  {scene.width} × {scene.height} px
                </dd>
              </div>
              <div>
                <dt>Кодировка</dt>
                <dd>
                  {encoded.status === 'ok' && encoded.matrix.eciUtf8 ? 'UTF-8, ECI 26' : 'ASCII'}
                </dd>
              </div>
            </dl>
          ) : null}

          {encodedPreview !== null ? (
            <div className={styles.payloadBox}>
              <h2 className={styles.payloadTitle}>Фактически закодировано</h2>
              <pre className={styles.payload}>{encodedPreview}</pre>
              {content.host ? (
                <p className={styles.host}>
                  Домен назначения: <strong>{content.host}</strong>. Мы не проверяем репутацию сайта и
                  не открываем его.
                </p>
              ) : null}
            </div>
          ) : null}

          <IssueList issues={layoutIssues} title={layoutIssues.length > 0 ? 'Макет' : undefined} />
        </section>

        {/* На узком экране этот блок прилипает к низу экрана и остаётся
            доступным при любой прокрутке. */}
        <div className={styles.exportDock}>
          <ExportBar
            job={exportJob}
            ready={ready}
            format={state.project.output.format}
            onExport={startExport}
          />
        </div>
      </div>
    </div>
  );
}
